import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/OrganSystemView.css";
import OrganSystemDashboard from "./OrganSystemDashboard/OrganSystemDashboard";
import OrganSystemLinePlot from "./OrganSystemLinePlot/OrganSystemLinePlot";
import OrganSystemHeatmap from "./OrganSystemHeatmap/OrganSystemHeatmap";
import OrganSystemInfoModal from "./OrganSystemInfoModal/OrganSystemInfoModal";
import { useFilters } from "../context/FilterProvider";
import { useScreen } from "../context/ScreenProvider";
import { getOrganSystemDescription } from "../data/organSystemDescriptions";
import bloodGroupings from "../../lambda/health-recommendations/blood_groupings.json";

function OrganSystemView({
    systemScoresArray,
    isLoadingScores,
    loadingFiles,
    bloodTestFiles,
    getFxRange,
    sex,
    selectedFileIndex,
    previousScores = {},
    onTestPillClick,
    onProtocolClick,
    onUploadClick,
    onManualEntry,
}) {
    const {
        selectedSystem, viewMode, highlightedSystems,
        selectSystem, forceSelectSystem, selectSystemAndOpenLineplot, setViewMode,
    } = useFilters();

    const { isMobile } = useScreen();

    const [showLineplotModal, setShowLineplotModal] = useState(false);
    const [showSystemModal, setShowSystemModal] = useState(false);
    const viewRef = useRef(null);
    const [viewHeight, setViewHeight] = useState(null);

    const focusedSystem = isMobile ? selectedSystem : null;

    useEffect(() => {
        if (isMobile || viewMode !== 'circles' || !viewRef.current) return;
        const frame = requestAnimationFrame(() => {
            const h = viewRef.current?.offsetHeight;
            if (h > 0) setViewHeight(h);
        });
        return () => cancelAnimationFrame(frame);
    }, [viewMode, isMobile, systemScoresArray, isLoadingScores]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key !== 'Escape') return;
            if (showSystemModal) {
                setShowSystemModal(false);
            } else if (showLineplotModal) {
                setShowLineplotModal(false);
            } else if (focusedSystem) {
                selectSystem(focusedSystem);
            } else if (!isMobile && viewMode !== 'circles') {
                setViewMode('circles');
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [showSystemModal, showLineplotModal, focusedSystem, isMobile, viewMode, selectSystem, setViewMode]);

    const getLowestSystem = () => {
        if (!systemScoresArray || systemScoresArray.length === 0) return null;
        const scoredSystems = systemScoresArray.filter(s => s.score != null);
        if (scoredSystems.length === 0) return null;
        return scoredSystems.reduce((lowest, current) =>
            current.score < lowest.score ? current : lowest
        );
    };

    const lowestSystem = getLowestSystem();

    const handleTimelineClick = (systemId) => {
        if (isMobile) {
            forceSelectSystem(systemId);
            setShowLineplotModal(true);
        } else {
            selectSystemAndOpenLineplot(systemId);
        }
    };

    return (
        <div className="organ-system-view">
            <div
                ref={viewRef}
                className="view-area"
                style={!isMobile && viewMode !== 'circles' ? { height: viewHeight || 350 } : undefined}
            >
                {isMobile || viewMode === 'circles' ? (
                    <OrganSystemDashboard
                        systemScoresArray={systemScoresArray}
                        selectedSystem={selectedSystem}
                        onSystemClick={selectSystem}
                        isLoading={isLoadingScores}
                        highlightedSystems={highlightedSystems}
                        previousScores={previousScores}
                        focusedSystem={focusedSystem}
                        onTestPillClick={onTestPillClick}
                        onTimelineClick={!isMobile ? handleTimelineClick : undefined}
                        onMoreInfoClick={() => setShowSystemModal(true)}
                        onProtocolClick={onProtocolClick}
                        onUploadClick={onUploadClick}
                        onManualEntry={onManualEntry}
                        bloodTestFiles={bloodTestFiles}
                        getFxRange={getFxRange}
                        sex={sex}
                        selectedFileIndex={selectedFileIndex}
                        loadingFiles={loadingFiles}
                    />
                ) : viewMode === 'lineplot' ? (
                    <OrganSystemLinePlot
                        bloodTestFiles={bloodTestFiles}
                        getFxRange={getFxRange}
                        sex={sex}
                        selectedSystem={selectedSystem}
                        onSystemClick={selectSystem}
                        selectedFileIndex={selectedFileIndex}
                    />
                ) : (
                    <OrganSystemHeatmap
                        bloodTestFiles={bloodTestFiles}
                        getFxRange={getFxRange}
                        sex={sex}
                        selectedSystem={selectedSystem}
                        onSystemClick={selectSystem}
                    />
                )}
            </div>

            {!isMobile && (
                <div className="system-summary-section">
                    {selectedSystem ? (
                        (() => {
                            const systemInfo = getOrganSystemDescription(selectedSystem);
                            return systemInfo ? (
                                <p className="osv-brief">
                                    {systemInfo.summary}{' '}
                                    <button
                                        className="see-more-link"
                                        onClick={() => setShowSystemModal(true)}
                                        style={{ display: 'inline' }}
                                    >
                                        ...More info
                                    </button>
                                </p>
                            ) : null;
                        })()
                    ) : (
                        <p className="osv-brief">
                            {lowestSystem
                                ? `Your ${lowestSystem.displayName} markers could use some attention.`
                                : "Upload a blood test to see your organ system health scores."
                            }
                        </p>
                    )}
                </div>
            )}

            {!isMobile && bloodTestFiles?.length > 1 && (
                <button
                    className="view-toggle-button"
                    onClick={() => setViewMode(viewMode === 'circles' ? 'lineplot' : 'circles')}
                    title={viewMode === 'circles' ? 'Show timeline' : 'Show circles'}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                </button>
            )}

            {isMobile && showLineplotModal && createPortal(
                <div className="lineplot-modal-backdrop" onClick={() => setShowLineplotModal(false)}>
                    <div className="lineplot-modal" onClick={e => e.stopPropagation()}>
                        <OrganSystemLinePlot
                            bloodTestFiles={bloodTestFiles}
                            getFxRange={getFxRange}
                            sex={sex}
                            selectedSystem={selectedSystem}
                            onSystemClick={selectSystem}
                            selectedFileIndex={selectedFileIndex}
                        />
                    </div>
                </div>,
                document.body
            )}

            {showSystemModal && selectedSystem && (() => {
                const systemInfo = getOrganSystemDescription(selectedSystem);
                return systemInfo ? (
                    <OrganSystemInfoModal
                        systemId={selectedSystem}
                        systemInfo={systemInfo}
                        markers={bloodGroupings[selectedSystem].markers}
                        onClose={() => setShowSystemModal(false)}
                    />
                ) : null;
            })()}
        </div>
    );
}

export default OrganSystemView;
